import {useQuery} from 'react-query'
import {deleteBus, fetchBusList} from './api'
import {PageTitle} from '../../../_metronic/layout/core'
import {Error500} from '../../modules/errors/components/Error500'
import CRUDTable from '../../../components/table/CRUDTable'
import BusForm from './Form'
import toast, {Toaster} from 'react-hot-toast'
import ModalWithForm from '../../../components/ModalWithForm'
import {Button} from 'react-bootstrap'
import viewProps from '../viewProps'
import {Processing} from '../../../components/Loaders'
import GPS from './GPS'
import PIR from './PIR'
import {Helmet} from 'react-helmet'
import NFC from './NFC'
import WithModal from '../../../components/WithModal'
import ViewAll from '../../../components/ViewAll'
import ActivePill from '../../../components/ActivePill'
import InactivePill from '../../../components/InactivePill'
import MoreInfoLabel from '../../../components/MoreInfoLabel'
import PortalDropdown from '../../../components/PortalDropdown'
import DeleteLabel from '../../../components/DeleteLabel'
import DeleteConfirmationModal from '../../../components/DeleteWarning'
import EditLabel from '../../../components/EditLabel'
import TopBarProgress from 'react-topbar-progress-indicator'

const busBreadcrumbs = [
  {
    title: 'School Management',
    path: 'school-management/bus',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

function Bus() {
  const {data, isError, isFetching, isLoading, refetch} = useQuery('busList', fetchBusList, {
    refetchOnWindowFocus: false,
    select: (response) => {
      if (response.data) {
        response.data.forEach((item) => {
          item.bus_insurance_expiry =
            item.bus_insurance_expiry === '0001-01-01' ? '' : item.bus_insurance_expiry
          item.bus_last_service =
            item.bus_last_service === '0001-01-01' ? '' : item.bus_last_service
          item.bus_polution_expiry =
            item.bus_polution_expiry === '0001-01-01' ? '' : item.bus_polution_expiry
        })
      }
      return response
    },
  })

  if (isLoading) {
    return <Processing />
  }

  const getDeviceInfo = (ids) => {
    const count = ids.filter(Boolean).length
    const iconColor = count === 2 ? 'text-primary' : ''
    const badgeColor = count === 2 ? 'bg-info text-white' : 'bg-gray-500 text-white'
    return {count, iconColor, badgeColor}
  }

  const DeviceItem = ({icon, label, info}) => (
    <div className="px-3 py-2 d-flex align-items-center">
      <span className="d-block position-relative">
        <i className={`${icon} fs-6 ${info.iconColor}`} />
        <span
          className={`position-absolute rounded-circle ${info.badgeColor}`}
          style={{
            top: '-8px',
            right: '-6px',
            width: '12px',
            height: '12px',
            fontSize: '8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: '0.8',
          }}
        >
          {info.count}
        </span>
      </span>
      <span className="ms-2">{label}</span>
    </div>
  )

  const cellModifier = {
    bus_status: ({value}) => {
      switch (Number(value)) {
        case 1:
          return <ActivePill />
        case 0:
          return <InactivePill />
        default:
          return ''
      }
    },
    more: ({row}) => {
      const orderedFields = [
        ...Object.entries(viewProps.Bus.primaryFieldsToLabelMap),
        ...Object.entries(viewProps.Bus.secondaryFieldsToMap),
      ]
      return (
        <WithModal
          renderModalBody={() => (
            <ViewAll data={row.original} cellModifier={cellModifier} fieldOrder={orderedFields} />
          )}
          modalTitle={
            <span>
              Details of{' '}
              <span className="text-primary">
                {row.original.bus_name} - {row.original.bus_number}
              </span>
            </span>
          }
          modalAttrs={{backdrop: true, size: 'lg'}}
        >
          <MoreInfoLabel />
        </WithModal>
      )
    },
    Actions: ({row}) => {
      const data = row.original
      const rfidInfo = getDeviceInfo([data.attendence_nfc_device_id, data.trip_nfc_device_id])
      const radarInfo = getDeviceInfo([data.pir_device_id1, data.pir_device_id2])
      const gpsInfo = getDeviceInfo([data.gps_device_id, data.gps_powerboard_id])

      const handleDelete = async () => {
        const toastId = toast.loading('Deleting Please wait...')
        try {
          const response = await deleteBus(data.bus_id)
          if (response?.status === false && response?.message) {
            toast.error(response.message, {id: toastId})
            return
          }
          await refetch()
          toast.success('Deleted Successfully', {id: toastId})
        } catch {
          toast.error('Something went wrong, please try again later', {id: toastId})
        }
      }

      return (
        <PortalDropdown triggerProps={{className: 'btn btn-secondary px-0 py-1'}}>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={
                <span>
                  Presence -{' '}
                  <span className="text-primary">
                    {data.bus_name} - {data.bus_number}
                  </span>
                </span>
              }
              initialValues={data}
              Form={PIR}
              onSubmit={refetch}
              triggerClassName="d-block"
            >
              <DeviceItem icon="bi bi-radioactive" label="Presence" info={radarInfo} />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={
                <span>
                  Device of{' '}
                  <span className="text-primary">
                    {data.bus_name} - {data.bus_number}
                  </span>
                </span>
              }
              initialValues={data}
              Form={GPS}
              modalAttrs={{size: 'sm'}}
              onSubmit={refetch}
              triggerClassName="d-block"
            >
              <DeviceItem icon="fa fa-map-location-dot" label="Device" info={gpsInfo} />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <ModalWithForm
              modalTitle={
                <span>
                  RFID of{' '}
                  <span className="text-primary">
                    {data.bus_name} - {data.bus_number}
                  </span>
                </span>
              }
              initialValues={data}
              Form={NFC}
              onSubmit={refetch}
              modalAttrs={{size: 'sm'}}
              triggerClassName="d-block"
            >
              <DeviceItem icon="fa-brands fa-nfc-symbol" label="RFID" info={rfidInfo} />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <ModalWithForm
              triggerClassName="d-block"
              modalTitle="Update Bus"
              initialValues={data}
              onSubmit={refetch}
              Form={BusForm}
            >
              <EditLabel />
            </ModalWithForm>
          </span>
          <span className="dropdown-item p-0">
            <DeleteConfirmationModal triggerClassName="d-block" onDelete={handleDelete}>
              <DeleteLabel />
            </DeleteConfirmationModal>
          </span>
        </PortalDropdown>
      )
    },
  }

  return (
    <>
      <Helmet>
        <title>TrackNAB | Bus list</title>
      </Helmet>
      <PageTitle breadcrumbs={busBreadcrumbs}>Bus</PageTitle>
      {isError ? (
        <div className="text-center">
          <Error500 />
        </div>
      ) : (
        <CRUDTable
          data={data?.data || []}
          {...viewProps.Bus}
          fieldToLabelMap={viewProps.Bus.primaryFieldsToLabelMap}
          Form={BusForm}
          formSize={{size: 'md'}}
          cellModifier={cellModifier}
          headerExtras={
            <ModalWithForm
              modalTitle={'Add Bus'}
              triggerClassName="ms-auto"
              onSubmit={refetch}
              Form={BusForm}
            >
              <Button size="sm">Add Bus</Button>
            </ModalWithForm>
          }
          nonSortableColumns={['More Info', 'Actions']}
        />
      )}
      <Toaster />
      {isFetching && <TopBarProgress />}
    </>
  )
}

export default Bus
